import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';

import { env } from './env';

export interface ApiError {
  status: number;
  code: string;
  message: string;
  details?: unknown;
  requestId?: string;
}

export const api: AxiosInstance = axios.create({
  baseURL: env.VITE_API_URL,
  withCredentials: true,
  timeout: 30_000,
});

let isRefreshing = false;
let refreshSubscribers: Array<(_token: string | null) => void> = [];
let accessToken: string | null = null;
let activeStoreId: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Set the active store id sent on every request as X-Store-Id. */
export function setActiveStoreId(id: string | null): void {
  activeStoreId = id;
}

function notifyAll(token: string | null): void {
  const queue = refreshSubscribers;
  refreshSubscribers = [];
  for (const cb of queue) cb(token);
}

async function refreshAccessToken(): Promise<string | null> {
  if (isRefreshing) {
    return new Promise<string | null>((resolve) => refreshSubscribers.push(resolve));
  }
  isRefreshing = true;
  try {
    const res = await axios.post<{ accessToken: string }>(
      `${env.VITE_API_URL}/auth/refresh`,
      {},
      { withCredentials: true },
    );
    accessToken = res.data.accessToken;
    notifyAll(accessToken);
    return accessToken;
  } catch (err: unknown) {
    const status =
      (err as { response?: { status?: number } } | null | undefined)?.response?.status ?? 0;
    // Only a genuine auth rejection (dead/invalid refresh token) ends the
    // session. Transient failures — 429 rate-limit, network errors, 5xx — must
    // NOT log the user out; keep the current token so in-flight requests can
    // recover once the condition clears.
    if (status === 401 || status === 403) {
      accessToken = null;
    }
    notifyAll(accessToken);
    return accessToken;
  } finally {
    isRefreshing = false;
  }
}

api.interceptors.request.use((config) => {
  config.headers = config.headers ?? {};
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  if (activeStoreId && !config.headers['X-Store-Id']) {
    config.headers['X-Store-Id'] = activeStoreId;
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  async (err: {
    response?: {
      status: number;
      data?: { error?: { code: string; message: string; details?: unknown; requestId?: string } };
    };
    config?: AxiosRequestConfig & { _retried?: boolean };
  }) => {
    const status = err.response?.status ?? 0;
    const original = err.config;

    // Auto-refresh on 401 once per request
    if (
      status === 401 &&
      original &&
      !original._retried &&
      !(original.url?.includes('/auth/refresh') || original.url?.includes('/auth/login'))
    ) {
      original._retried = true;
      const fresh = await refreshAccessToken();
      if (fresh) {
        original.headers = { ...(original.headers ?? {}), Authorization: `Bearer ${fresh}` };
        return api.request(original);
      }
    }

    const errBody = err.response?.data?.error;
    const apiError: ApiError = {
      status,
      code: errBody?.code ?? 'UNKNOWN',
      message: errBody?.message ?? 'Beklenmeyen bir hata oluştu',
      details: errBody?.details,
      requestId: errBody?.requestId,
    };
    return Promise.reject(apiError);
  },
);
